/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { type ReactNode, useState } from 'react';

import { jsx } from '@compiled/react';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Grid, Pressable, xcss } from '@atlaskit/primitives';
import type { SearchPageNode } from '@atlassian/dynamic-cards-search-client';

import { type PrimaryProductKey } from '../../constants/products';
import { useAppContext } from '../app-context';
import { addVisitedLink, getVisitedLinks, isVisited } from './utils';

const containerStyles = xcss({
	padding: 'space.100',
	borderRadius: 'radius.small',
});

const iconAreaStyles = xcss({
	gridArea: 'icon',
	width: 'size.200',
	height: '100%',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'right',
});

const headingAreaStyles = xcss({
	gridArea: 'heading',
	overflow: 'hidden',
	whiteSpace: 'nowrap',
	textOverflow: 'ellipsis',
	font: 'font.body.large',
});

const subheadingStyles = xcss({
	color: 'color.text.subtle',
	marginTop: 'space.025',
	font: 'font.body',
	overflow: 'clip',
	overflowClipMargin: '5px', // space for focus outlines
});

const subheadingLeftAreaStyles = xcss({
	gridArea: 'subheading-left',
	display: 'flex',
	alignItems: 'center',
	overflow: 'hidden',
	whiteSpace: 'nowrap',
	textOverflow: 'ellipsis',
});

const bodyAreaStyles = xcss({
	gridArea: 'body',
	marginTop: 'space.075',
	overflowWrap: 'break-word',
});

const footerAreaStyles = xcss({
	gridArea: 'footer',
});

const visitedLinkStyles = xcss({
	textDecoration: 'none',
	':visited:active': {
		color: 'color.link.visited.pressed',
	},
	':visited': {
		color: 'color.link.visited',
	},
});
const visitedLinkPatchStyles = xcss({
	color: 'color.link.visited',
	':active': {
		color: 'color.link.visited.pressed',
	},
});

type SearchResultProps = {
	cloudId?: string;
	description: ReactNode;
	footer?: ReactNode;
	headerMetadata?: ReactNode;
	icon: ReactNode;
	id?: string;
	isThirdParty?: boolean;
	/** Called when the result is clicked */
	onResultClicked?: VoidFunction;
	/** Called when the result context menu is opened */
	onResultContextMenu?: VoidFunction;
	partial?: SearchPageNode;
	position?: number;
	primaryProduct?: PrimaryProductKey;
	showAISummary?: boolean;
	title: ReactNode;

	url?: string;
};

/**
 * This component is intended to be as generic as possible.
 * It should provide generic "slots" for users of this component to add
 * their content in.
 */
export const SearchResult = ({
	id,
	primaryProduct: _primaryProduct,
	description,
	footer,
	icon,
	headerMetadata = [],
	title,
	url,
	isThirdParty = false,
	showAISummary: _showAISummary = false,
	cloudId: _cloudId,
	onResultClicked,
	onResultContextMenu,
}: SearchResultProps): JSX.Element => {
	const { setIsHovered, setIsFocused } = useVisibilityState();

	/**
	 * A dynamic search_id query param and queryHash is appended to the document URL for 1P search results.
	 * As result we are unable to use the browser's visited links history to determine if a link has been visited.
	 * This is workaround until we have a better solution.
	 */
	const visitedLinks = getVisitedLinks();
	const isVisitedLink = id ? isVisited(visitedLinks, id) : false;
	const { linkComponent: Link } = useAppContext();

	return (
		<Box
			role="listitem"
			xcss={containerStyles}
			onMouseEnter={() => setIsHovered(true)}
			onMouseLeave={() => setIsHovered(false)}
			onFocusCapture={() => setIsFocused(true)}
			onBlurCapture={() => setIsFocused(false)}
			testId="search-page-result"
		>
			<Grid
				columnGap="space.150"
				templateAreas={[
					'icon heading header-right',
					'icon subheading-left header-right',
					'. body body',
					'. footer footer',
				]}
				templateColumns="auto 1fr auto"
			>
				<Box xcss={iconAreaStyles}>{icon}</Box>
				{id && url ? (
					<Link
						xcss={[headingAreaStyles, visitedLinkStyles, isVisitedLink && visitedLinkPatchStyles]}
						rel={isThirdParty ? 'noreferrer' : ''}
						target={isThirdParty ? '_blank' : undefined}
						href={url}
						testId="search-page-result-link"
						onClick={() => {
							addVisitedLink(id);
							onResultClicked?.();
						}}
						onContextMenu={onResultContextMenu}
					>
						{title}
					</Link>
				) : onResultClicked ? (
					<Pressable
						xcss={headingAreaStyles}
						onClick={onResultClicked}
						onContextMenu={onResultContextMenu}
					>
						{title}
					</Pressable>
				) : (
					<Box xcss={headingAreaStyles}>{title}</Box>
				)}
				{headerMetadata && (
					<Box
						xcss={[subheadingLeftAreaStyles, subheadingStyles]}
						testId="search-page-result-metadata"
					>
						{headerMetadata}
					</Box>
				)}
				<Box xcss={bodyAreaStyles}>{description}</Box>
				<Box xcss={footerAreaStyles}>{footer}</Box>
			</Grid>
		</Box>
	);
};

const useVisibilityState = () => {
	const [isHovered, setIsHovered] = useState(false);
	const [isFocused, setIsFocused] = useState(false);
	const [isContextMenuOpen, setIsContextMenuOpen] = useState(false);

	return {
		isContextMenuVisible: isHovered || isFocused || isContextMenuOpen,
		isContextMenuOpen,
		setIsHovered,
		setIsFocused,
		setIsContextMenuOpen,
	};
};
